import { CSSProperties, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, LogIn, LogOut, Menu } from "lucide-react";
import {
  sidebarItems, sidebarFooterItems, sichtbareEintraege, SidebarItem,
} from "../config/sidebar";
import { clearToken } from "../api";
import { useAuthed, useIch } from "../lib/useAuthed";
import { darf } from "../api";
import { useDialog } from "../components/Dialog";
import { useOffeneFragen } from "../features/reports/fragen";
import { zielFuer } from "../lib/letzterOrt";

const OPEN_GROUPS_KEY = "home.sidebar.openGroups";

type Props = {
  /** Called when an item is clicked — used by the mobile drawer to close itself. */
  onNavigate?: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** When the global vim-focus is on the sidebar, this is the index into
      the flattened [sidebarItems, ...sidebarFooterItems] list that should
      render with a focus ring. `null` means the sidebar is not vim-focused. */
  vimFocusedIndex?: number | null;
  /** Öffnet den Anmelde-Dialog; nur im abgemeldeten Zustand gezeigt. */
  onLogin?: () => void;
  /** Schlaegt die Hilfe zur aktuellen Seite auf. */
  onHilfe?: () => void;
};

export function Sidebar({ onNavigate, collapsed, onToggleCollapsed, vimFocusedIndex = null, onLogin, onHilfe }: Props) {
  const authed = useAuthed();
  // Wie viele Rueckfragen unbeantwortet sind. Ohne diese Zahl merkt niemand,
  // dass in den Meldungen eine Frage wartet — und eine Frage, die niemand
  // sieht, ist keine.
  const offeneFragen = useOffeneFragen();
  // Ohne Anmeldung fehlen die privaten Einträge ganz. Ausgegraut wären sie
  // eine Liste dessen, was man nicht darf — eine kürzere Leiste sagt
  // schlicht, was es hier gibt. Die Reihenfolge trägt zugleich die Nummern
  // für die Tastaturnavigation, deshalb wird hier gefiltert und nicht erst
  // beim Rendern.
  const ich = useIch();
  const sichtbar = useMemo(
    () => sichtbareEintraege(sidebarItems, authed, darf),
    // `ich` steht in der Liste, damit sich die Leiste ändert, sobald der
    // Verwalter etwas freigibt oder wegnimmt.
    [authed, ich],
  );
  const sichtbarUnten = useMemo(
    () => sichtbareEintraege(sidebarFooterItems, authed, darf),
    [authed, ich],
  );
  // Scroll the vim-focused row into view so j/k always tracks visibly even
  // when the sidebar overflows on short viewports.
  useEffect(() => {
    if (vimFocusedIndex == null) return;
    const el = document.querySelector<HTMLElement>(`[data-vim-idx="${vimFocusedIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [vimFocusedIndex]);
  const nav = useNavigate();
  const dialog = useDialog();
  const loc = useLocation();
  // Shared group-open state. Defaults to "open" if no explicit close was
  // saved, OR if a child route is currently active.
  const [closedGroups, setClosedGroups] = useState<Set<string>>(() => {
    // Migration: we now store *closed* groups so brand-new groups default
    // to expanded. The legacy key (home.sidebar.openGroups) is honoured by
    // simply ignoring it on first run.
    try {
      const raw = localStorage.getItem("home.sidebar.closedGroups");
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch { return new Set(); }
  });

  useEffect(() => {
    try {
      localStorage.setItem(
        "home.sidebar.closedGroups",
        JSON.stringify(Array.from(closedGroups)),
      );
      // Drop the obsolete key once we've written the new one.
      localStorage.removeItem(OPEN_GROUPS_KEY);
    } catch {/* swallow */}
  }, [closedGroups]);

  const toggleGroup = (id: string) => {
    setClosedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  async function logout() {
    const ok = await dialog.confirm({
      title: "Sign out?",
      message: "You will be taken to the sign-in page.",
      confirmLabel: "Sign out",
      danger: true,
    });
    if (!ok) return;
    clearToken();
    onNavigate?.();
    nav("/login", { replace: true });
  }

  const isOpen = (item: SidebarItem) => {
    if (closedGroups.has(item.id)) return false;
    // Force-open if a child path is currently active so the user always sees
    // their location in the tree.
    if (item.children?.some((c) => loc.pathname.startsWith(c.path))) return true;
    return true;
  };

  return (
    <nav className="sidebar-inner">
      <button
        className="sidebar-toggle"
        onClick={onToggleCollapsed}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-expanded={!collapsed}
      >
        {/* Wrapped in `.icon` so its width matches the nav items below —
            keeps the hamburger and the section icons in the same column.
            The brand-text is always rendered; CSS hides it in collapsed
            mode via `display: none` so React doesn't tear down the DOM
            mid-animation (was causing a visible flicker). */}
        <span className="icon"><Menu className="icon-svg" /></span>
        <span className="sidebar-brand-text">home</span>
      </button>

      <ul className="sidebar-list">
        {sichtbar.map((item, i) => (
          <SidebarEntry
            key={item.id}
            item={item}
            onNavigate={onNavigate}
            collapsed={collapsed}
            open={isOpen(item)}
            onToggle={() => toggleGroup(item.id)}
            vimIndex={i}
            vimFocused={vimFocusedIndex === i}
          />
        ))}
      </ul>

      <div className="sidebar-footer">
        <ul className="sidebar-list sidebar-footer-list">
          {sichtbarUnten.map((item, i) => {
            const vi = sichtbar.length + i;
            return (
              <SidebarEntry
                key={item.id}
                item={item}
                onNavigate={onNavigate}
                collapsed={collapsed}
                open={isOpen(item)}
                onToggle={() => toggleGroup(item.id)}
                vimIndex={vi}
                vimFocused={vimFocusedIndex === vi}
                onAktion={(was) => { if (was === "hilfe") onHilfe?.(); }}
                marke={item.id === "reports" ? offeneFragen : undefined}
              />
            );
          })}
          <li
            className="sidebar-footer-logout"
            style={{
              ["--sidebar-color" as string]: authed ? "var(--red)" : "var(--green)",
            } as CSSProperties}
          >
            {authed ? (
              <button onClick={logout} title={collapsed ? "Sign out" : undefined}>
                <span className="icon"><LogOut className="icon-svg" /></span>
                <span>Sign out</span>
              </button>
            ) : (
              <button
                onClick={() => { onNavigate?.(); onLogin?.(); }}
                title={collapsed ? "Sign in" : undefined}
              >
                <span className="icon"><LogIn className="icon-svg" /></span>
                <span>Sign in</span>
              </button>
            )}
          </li>
        </ul>
      </div>
    </nav>
  );
}

function colorStyle(c?: string): CSSProperties | undefined {
  // Both the named token (`--sidebar-color`) and its RGB triplet
  // (`--sidebar-color-rgb`) are exposed so child CSS can use the colour
  // either as a solid (icon, active rail) or as a tinted rgba()
  // background (hover/active fill).
  return c
    ? ({
        ["--sidebar-color" as string]: `var(--${c})`,
        ["--sidebar-color-rgb" as string]: `var(--${c}-rgb)`,
      } as CSSProperties)
    : undefined;
}

function SidebarEntry({
  item, onNavigate, collapsed, open, onToggle, vimIndex, vimFocused,
  onAktion, marke,
}: {
  item: SidebarItem;
  onNavigate?: () => void;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
  vimIndex: number;
  vimFocused: boolean;
  /** Fuer Eintraege, die etwas aufschlagen statt irgendwohin zu fuehren. */
  onAktion?: (was: NonNullable<SidebarItem["aktion"]>) => void;
  /** Eine Zahl neben dem Namen, wenn dort etwas wartet. */
  marke?: number;
}) {
  // Wo man gerade steht, entscheidet, wohin ein Verweis fuehrt: in den
  // eigenen Bereich an den Anfang, in einen fremden dorthin zurueck, wo man
  // zuletzt war. Siehe lib/letzterOrt.
  const jetzt = useLocation().pathname;
  const linkVimCls = vimFocused ? " is-vim-focused" : "";
  // Zwei Plaetze fuer dieselbe Zahl: in der breiten Leiste am Zeilenende,
  // wo Platz ist; in der schmalen am Symbol, weil es dort nichts anderes
  // gibt, woran sie haengen koennte.
  const text = marke && marke > 0 ? (marke > 99 ? "99+" : String(marke)) : null;
  const zahlAmSymbol = text ? <span className="sidebar-marke">{text}</span> : null;
  const zahlAmRand = text
    ? <span className="sidebar-marke sidebar-marke--rand">{text}</span>
    : null;

  // Ein Griff statt eines Ziels: derselbe Zeilenaufbau, aber ein Knopf.
  if (item.aktion) {
    return (
      <li data-vim-idx={vimIndex} style={colorStyle(item.color)}>
        <button
          type="button"
          className={`sidebar-aktion${linkVimCls}`}
          title={collapsed ? item.label : undefined}
          onClick={() => { onNavigate?.(); onAktion?.(item.aktion!); }}
        >
          <span className="icon">{item.icon}</span>
          <span>{item.label}</span>
        </button>
      </li>
    );
  }

  // Collapsed mode: only the parent icon is shown.
  if (collapsed) {
    return (
      <li data-vim-idx={vimIndex} style={colorStyle(item.color)}>
        <NavLink
          to={zielFuer(item.path, jetzt)}
          end={item.path === "/"}
          onClick={onNavigate}
          title={marke ? `${item.label} (${marke})` : item.label}
          className={linkVimCls.trim() || undefined}
        >
          <span className="icon">{item.icon}{zahlAmSymbol}</span>
          <span>{item.label}</span>
        </NavLink>
      </li>
    );
  }

  const hasChildren = !!item.children && item.children.length > 0;

  return (
    <li data-vim-idx={vimIndex} style={colorStyle(item.color)}>
      <div className="sidebar-row">
        <NavLink
          to={zielFuer(item.path, jetzt)}
          end={item.path === "/"}
          onClick={onNavigate}
          className={`sidebar-row-link${linkVimCls}`}
        >
          <span className="icon">{item.icon}</span>
          <span>{item.label}</span>
          {zahlAmRand}
        </NavLink>
        {hasChildren && (
          <button
            type="button"
            className="sidebar-row-chevron"
            onClick={onToggle}
            aria-label={open ? "Collapse submenu" : "Expand submenu"}
            aria-expanded={open}
          >
            {open
              ? <ChevronDown className="icon-svg" />
              : <ChevronRight className="icon-svg" />}
          </button>
        )}
      </div>
      {hasChildren && open && (
        <ul className="sidebar-children">
          {item.children!.map((c) => (
            <li key={c.id} style={colorStyle(c.color)}>
              <NavLink to={zielFuer(c.path, jetzt)} onClick={onNavigate}>
                <span className="icon">{c.icon}</span>
                <span>{c.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
